import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Search, RefreshCw, X, KeyRound, Send, Trash2, Mail, Phone, Calendar, Package, Heart, Coins } from "lucide-react";

const inputDark = "w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-cream placeholder:text-cream/60 focus:border-[#C9789A] focus:outline-none transition";

const AdminCustomers = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // detailed customer
  const [editForm, setEditForm] = useState({});
  const [savingPwd, setSavingPwd] = useState(false);
  const [coinData, setCoinData] = useState(null);
  const [coinDelta, setCoinDelta] = useState("");
  const [coinNote, setCoinNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/customers", { params: { search: search || undefined } });
      setItems(r.data);
    } catch { toast.error("Could not load customers"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/admin/customers/${id}`);
      setEditing(r.data);
      setEditForm({
        full_name: r.data.full_name || "",
        email: r.data.email || "",
        phone: r.data.phone || "",
        new_password: "",
        send_credentials_email: false,
      });
      // Load coin data for this customer
      try {
        const cr = await api.get(`/admin/customers/${id}/coins`);
        setCoinData(cr.data);
      } catch { setCoinData(null); }
      setCoinDelta(""); setCoinNote("");
    } catch { toast.error("Could not load customer"); }
  };

  const adjustCoins = async () => {
    if (!editing) return;
    const delta = parseInt(coinDelta, 10);
    if (!delta) { toast.error("Enter a non-zero amount"); return; }
    try {
      await api.post(`/admin/customers/${editing.id}/coins/adjust`, {
        delta, note: coinNote || "Manual admin adjustment",
      });
      toast.success(`${delta > 0 ? "Credited" : "Debited"} ${Math.abs(delta)} coins`);
      const cr = await api.get(`/admin/customers/${editing.id}/coins`);
      setCoinData(cr.data);
      setCoinDelta(""); setCoinNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not adjust");
    }
  };

  const saveDetail = async () => {
    if (!editing) return;
    setSavingPwd(true);
    try {
      const payload = { ...editForm };
      if (!payload.new_password) { delete payload.new_password; delete payload.send_credentials_email; }
      const r = await api.patch(`/admin/customers/${editing.id}`, payload);
      toast.success(payload.new_password ? "Saved · password updated" : "Customer updated");
      setEditing({ ...editing, ...r.data });
      setEditForm({ ...editForm, new_password: "", send_credentials_email: false });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSavingPwd(false); }
  };

  const resetAndEmail = async () => {
    if (!editing) return;
    if (!window.confirm(`Generate a fresh password and email it to ${editing.email}?`)) return;
    try {
      const r = await api.post(`/admin/customers/${editing.id}/reset-password`);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset");
    }
  };

  const deleteCustomer = async () => {
    if (!editing) return;
    if (!window.confirm(`Permanently delete ${editing.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/customers/${editing.id}`);
      toast.success("Customer deleted");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div data-testid="admin-customers-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9789A] mb-2">Audience</p>
          <h1 className="font-hero text-3xl text-cream">Customers</h1>
          <p className="text-sm text-cream/75 mt-1">{items.length} {items.length === 1 ? "customer" : "customers"} · click any row to manage details, reset password, or view orders.</p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.22em] text-cream/70 border border-white/10 hover:bg-white/5"
          data-testid="admin-customers-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="mb-5 flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/65" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name, email or phone…"
            className={`${inputDark} pl-9`}
            data-testid="admin-customers-search"
          />
        </div>
        <button
          onClick={load}
          className="px-4 py-2 rounded-lg text-xs uppercase tracking-[0.2em] bg-[#C9789A]/80 text-white hover:bg-[#C9789A]"
        >
          Search
        </button>
      </div>

      <div className="bg-white/[0.08] rounded-2xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="text-left font-label text-[10px] uppercase tracking-[0.22em] text-cream/65 border-b border-white/10">
              <th className="py-3 px-4">Customer</th>
              <th>Phone</th>
              <th>Orders</th>
              <th>Lifetime spend</th>
              <th>Joined</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="py-8 text-center text-cream/65">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-cream/65">No customers match.</td></tr>
            )}
            {items.map((c) => (
              <tr
                key={c.id}
                onClick={() => openDetail(c.id)}
                className="border-b border-white/10 last:border-b-0 hover:bg-white/[0.08] cursor-pointer"
                data-testid={`admin-customer-row-${c.id}`}
              >
                <td className="py-3 px-4">
                  <p className="font-cormorant text-lg text-cream">{c.full_name || "Guest"}</p>
                  <p className="text-xs text-cream/75">{c.email}</p>
                  {c.auto_provisioned && (
                    <span className="text-[9px] uppercase tracking-[0.22em] text-[#D4B896] bg-[#D4B896]/10 px-2 py-0.5 rounded-full mt-1 inline-block">
                      Created at checkout
                    </span>
                  )}
                </td>
                <td className="text-cream/70">{c.phone || "—"}</td>
                <td className="text-cream/80">{c.order_count}</td>
                <td className="font-cormorant text-base text-cream">{formatINR(c.lifetime_value || 0)}</td>
                <td className="text-cream/75 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="text-[#C9789A] text-xs uppercase tracking-[0.22em] pr-4">Manage →</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      <AnimatePresence>
        {editing && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-40"
              onClick={() => setEditing(null)}
            />
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 right-0 z-50 h-full w-[min(620px,94vw)] bg-[#1a0e15] border-l border-white/10 overflow-y-auto"
              data-testid="admin-customer-drawer"
            >
              <div className="sticky top-0 bg-[#1a0e15] border-b border-white/10 px-7 py-5 flex items-start justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.26em] text-[#C9789A] mb-1">Customer</p>
                  <h2 className="font-hero text-2xl text-cream">{editing.full_name || "Guest"}</h2>
                  <p className="text-xs text-cream/75 font-mono mt-0.5">ID: {editing.id}</p>
                </div>
                <button onClick={() => setEditing(null)} className="text-cream/75 hover:text-cream">
                  <X size={20} />
                </button>
              </div>

              <div className="p-7 space-y-7">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <Stat icon={Package} label="Orders" value={editing.order_count} />
                  <Stat icon={Heart} label="Wishlist" value={(editing.wishlist || []).length} />
                  <Stat icon={Calendar} label="Joined" value={new Date(editing.created_at).toLocaleDateString()} />
                </div>

                {/* Edit profile */}
                <div className="space-y-3">
                  <p className="eyebrow text-cream/75">Edit details</p>
                  <Field label="Full name" value={editForm.full_name} onChange={(v) => setEditForm({ ...editForm, full_name: v })} testId="admin-cust-name" />
                  <Field label="Email" type="email" value={editForm.email} onChange={(v) => setEditForm({ ...editForm, email: v })} testId="admin-cust-email" />
                  <Field label="Phone" value={editForm.phone} onChange={(v) => setEditForm({ ...editForm, phone: v })} testId="admin-cust-phone" />
                </div>

                {/* Password reset */}
                <div className="space-y-3 p-5 rounded-xl bg-black/20 border border-white/10">
                  <p className="eyebrow text-[#C9789A] flex items-center gap-2"><KeyRound size={12} /> Set password</p>
                  <Field
                    label="New password (leave blank to keep existing)"
                    type="password"
                    value={editForm.new_password}
                    onChange={(v) => setEditForm({ ...editForm, new_password: v })}
                    testId="admin-cust-new-password"
                  />
                  <label className="flex items-center gap-2 text-xs text-cream/70 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editForm.send_credentials_email}
                      onChange={(e) => setEditForm({ ...editForm, send_credentials_email: e.target.checked })}
                      className="accent-[#C9789A]"
                      data-testid="admin-cust-send-creds"
                    />
                    Email these credentials to the customer
                  </label>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={saveDetail}
                    disabled={savingPwd}
                    className="px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.22em] font-medium text-white"
                    style={{ background: "linear-gradient(135deg, #C9789A, #D4B896)" }}
                    data-testid="admin-cust-save"
                  >
                    {savingPwd ? "Saving…" : "Save changes"}
                  </button>
                  <button
                    onClick={resetAndEmail}
                    className="px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.22em] border border-[#C9789A] text-[#C9789A] hover:bg-[#C9789A]/10 inline-flex items-center gap-2"
                    data-testid="admin-cust-reset-email"
                  >
                    <Send size={12} /> Reset & email new password
                  </button>
                  <button
                    onClick={deleteCustomer}
                    className="px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.22em] border border-red-500/40 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-2 ml-auto"
                    data-testid="admin-cust-delete"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>

                {/* Coin wallet */}
                <div className="space-y-3 p-5 rounded-xl bg-black/20 border border-white/10" data-testid="admin-cust-coins">
                  <p className="eyebrow text-[#D4B896] flex items-center gap-2"><Coins size={12} /> Coin wallet</p>
                  <p className="font-hero text-3xl text-cream">{coinData?.balance ?? 0} <span className="text-sm text-cream/65 font-body">coins</span></p>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="number"
                      placeholder="±delta"
                      value={coinDelta}
                      onChange={(e) => setCoinDelta(e.target.value)}
                      className={inputDark}
                      data-testid="admin-cust-coin-delta"
                    />
                    <input
                      type="text"
                      placeholder="Note (e.g. Goodwill bonus)"
                      value={coinNote}
                      onChange={(e) => setCoinNote(e.target.value)}
                      className={`${inputDark} col-span-2`}
                      data-testid="admin-cust-coin-note"
                    />
                  </div>
                  <button
                    onClick={adjustCoins}
                    className="px-4 py-2 rounded-full text-xs uppercase tracking-[0.22em] border border-[#D4B896] text-[#D4B896] hover:bg-[#D4B896]/10"
                    data-testid="admin-cust-coin-apply"
                  >
                    Apply adjustment
                  </button>
                  {coinData?.transactions?.length > 0 && (
                    <details className="text-xs text-cream/60">
                      <summary className="cursor-pointer">Recent {coinData.transactions.length} entries</summary>
                      <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                        {coinData.transactions.slice(0, 20).map(t => (
                          <div key={t.id} className="flex justify-between border-b border-white/10 py-1">
                            <span className="truncate flex-1">{t.note}</span>
                            <span className={t.delta >= 0 ? "text-emerald-400" : "text-rose"}>
                              {t.delta >= 0 ? "+" : ""}{t.delta}
                            </span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>

                {/* Recent orders */}
                <div>
                  <p className="eyebrow text-cream/75 mb-3">Recent orders</p>
                  {(editing.orders || []).length === 0 ? (
                    <p className="text-sm text-cream/65">No orders yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {editing.orders.slice(0, 8).map((o) => (
                        <div key={o.id} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.06] border border-white/10">
                          <div>
                            <p className="font-cormorant text-base text-cream">{o.order_number}</p>
                            <p className="text-xs text-cream/75">{new Date(o.created_at).toLocaleDateString()} · {o.items?.length || 0} item(s)</p>
                          </div>
                          <div className="text-right">
                            <p className="font-cormorant text-base text-cream">{formatINR(o.total)}</p>
                            <span className="text-[10px] uppercase tracking-[0.22em] text-cream/60">{o.payment_status} · {o.order_status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="p-4 rounded-xl bg-white/[0.06] border border-white/10">
    <Icon size={14} className="text-[#C9789A] mb-2" />
    <p className="text-[10px] uppercase tracking-[0.22em] text-cream/65 mb-0.5">{label}</p>
    <p className="font-cormorant text-lg text-cream">{value}</p>
  </div>
);

const Field = ({ label, value, onChange, type = "text", testId }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.24em] text-cream/65 mb-2">{label}</label>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-cream placeholder:text-cream/60 focus:border-[#C9789A] focus:outline-none transition"
      data-testid={testId}
    />
  </div>
);

export default AdminCustomers;
